export const ACADEMIC_YEAR_TIME_ZONE = 'Europe/Dublin';

const ACADEMIC_YEAR_START_MONTH = 7;
const ACADEMIC_YEAR_START_DAY = 30;

const dublinDateFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: ACADEMIC_YEAR_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hourCycle: 'h23',
});

const toValidDate = (value, name) => {
  if (value === undefined || value === null || value === '') {
    throw new TypeError(`${name} is required`);
  }

  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new RangeError(`${name} must be a valid date`);
  }

  return date;
};

const getDublinDateParts = (date) =>
  Object.fromEntries(
    dublinDateFormatter
      .formatToParts(date)
      .filter(({ type }) => type !== 'literal')
      .map(({ type, value }) => [type, Number(value)])
  );

// Converts a Dublin wall-clock date/time to its corresponding UTC instant.
// Iterating accounts for Ireland's daylight-saving offset without relying on
// the browser's local timezone.
const getDublinDate = (year, month, day) => {
  const targetTime = Date.UTC(year, month - 1, day, 0, 0, 0);
  let instant = targetTime;

  for (let index = 0; index < 3; index += 1) {
    const parts = getDublinDateParts(new Date(instant));
    const representedTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );
    instant += targetTime - representedTime;
  }

  return new Date(instant);
};

const createAcademicYear = (startYear) => {
  const endYear = startYear + 1;
  const startShort = String(startYear % 100).padStart(2, '0');
  const endShort = String(endYear % 100).padStart(2, '0');

  return {
    startYear,
    endYear,
    storage: `${startShort}-${endShort}`,
    display: `${startShort}/${endShort}`,
    full: `${startYear}-${endYear}`,
    fullDisplay: `${startYear}/${endShort}`,
  };
};

/**
 * Derives the academic year from an explicit instant using the Europe/Dublin
 * calendar. Callers using the browser clock must pass `new Date()` themselves;
 * this keeps tests deterministic and prevents the device timezone changing the
 * 30 July boundary.
 */
export const getAcademicYear = (asOfDate) => {
  const parts = getDublinDateParts(toValidDate(asOfDate, 'asOfDate'));
  const isOnOrAfterBoundary =
    parts.month > ACADEMIC_YEAR_START_MONTH ||
    (parts.month === ACADEMIC_YEAR_START_MONTH &&
      parts.day >= ACADEMIC_YEAR_START_DAY);

  return createAcademicYear(isOnOrAfterBoundary ? parts.year : parts.year - 1);
};

export const parseAcademicYear = (value) => {
  if (typeof value !== 'string') {
    throw new TypeError('academic year must be a string');
  }

  const match = value.trim().match(/^(\d{2}|\d{4})\s*[-/]\s*(\d{2}|\d{4})$/);
  if (!match) {
    throw new RangeError(`Invalid academic year: ${value}`);
  }

  const startYear = match[1].length === 2 ? 2000 + Number(match[1]) : Number(match[1]);
  const expectedEndYear = startYear + 1;
  const suppliedEndYear = Number(match[2]);
  const hasValidEndYear =
    match[2].length === 2
      ? suppliedEndYear === expectedEndYear % 100
      : suppliedEndYear === expectedEndYear;

  if (!hasValidEndYear) {
    throw new RangeError(`Invalid academic year: ${value}`);
  }

  return createAcademicYear(startYear);
};

export const formatAcademicYear = (value, format = 'display') => {
  const academicYear =
    typeof value === 'string'
      ? parseAcademicYear(value)
      : value && Number.isInteger(value.startYear)
        ? createAcademicYear(value.startYear)
        : null;

  if (!academicYear) {
    throw new TypeError('academic year must be a supported string or parsed academic year');
  }

  if (!['display', 'storage', 'full', 'fullDisplay'].includes(format)) {
    throw new RangeError(`Unsupported academic-year format: ${format}`);
  }

  return academicYear[format];
};

export const getAcademicYearRange = (asOfDate) => {
  const academicYear = getAcademicYear(asOfDate);

  return {
    ...academicYear,
    start: getDublinDate(
      academicYear.startYear,
      ACADEMIC_YEAR_START_MONTH,
      ACADEMIC_YEAR_START_DAY
    ),
    end: getDublinDate(
      academicYear.endYear,
      ACADEMIC_YEAR_START_MONTH,
      ACADEMIC_YEAR_START_DAY
    ),
  };
};

export const getAcademicYearOptions = ({
  asOfDate,
  previousYears = 0,
  futureYears = 0,
}) => {
  if (!Number.isInteger(previousYears) || previousYears < 0) {
    throw new RangeError('previousYears must be a non-negative integer');
  }
  if (!Number.isInteger(futureYears) || futureYears < 0) {
    throw new RangeError('futureYears must be a non-negative integer');
  }

  const currentYear = getAcademicYear(asOfDate);
  return Array.from(
    { length: previousYears + futureYears + 1 },
    (_, index) => currentYear.startYear + futureYears - index
  ).map((startYear) => {
    const academicYear = createAcademicYear(startYear);
    return {
      ...academicYear,
      start: getDublinDate(startYear, ACADEMIC_YEAR_START_MONTH, ACADEMIC_YEAR_START_DAY),
      end: getDublinDate(startYear + 1, ACADEMIC_YEAR_START_MONTH, ACADEMIC_YEAR_START_DAY),
    };
  });
};

export const isDateInAcademicYear = (date, academicYear) => {
  const value = toValidDate(date, 'date');
  const parsedYear =
    typeof academicYear === 'string'
      ? parseAcademicYear(academicYear)
      : academicYear && Number.isInteger(academicYear.startYear)
        ? createAcademicYear(academicYear.startYear)
        : null;

  if (!parsedYear) {
    throw new TypeError('academicYear must be a supported string or parsed academic year');
  }

  const start = getDublinDate(
    parsedYear.startYear,
    ACADEMIC_YEAR_START_MONTH,
    ACADEMIC_YEAR_START_DAY
  );
  const end = getDublinDate(
    parsedYear.endYear,
    ACADEMIC_YEAR_START_MONTH,
    ACADEMIC_YEAR_START_DAY
  );

  return value >= start && value < end;
};
